import './AgendaListPage.scss';
import { SimpleNumberCard, Checkbox, Button, Icon } from '@design-system/index';
import {
    AddDashed,
    ProductsCatalogLine,
    RoundedArrow,
    PoubelleLine,
    PoubelleFull,
    DupliquerLine,
    DupliquerFull,
    SyncroError
} from '@icons';
import { MutationTable } from '@components/Table/MutationTable/MutationTable';
import { AgendaProductTable } from '@services/product/product/product.types';
import { ColumnDef } from '@tanstack/react-table';
import { useGetAgendaProductsMutation } from '@services/product/product/product.endpoints';
import { LargeIconButton } from '@design-system/components/Buttons/LargeIconButton/LargeIconButton';
import { CATEGORY_TYPE } from '@utils/enumTranslation';
import { formatMoney } from '@utils/numbers';
import { useAgendaListPageVM } from './useAgendaListPageVM';
import { ImageLazy } from '@components/ImageLazy/ImageLazy';
import moment from 'moment';

export default function ListPage() {
    const { totalEvents, handleMetadata, columnHelper, needReload, navigate, handleDeleteProduct, handleDuplicateProduct, isLoading, handleDeleteProducts } =
        useAgendaListPageVM();

    const columns = [
        columnHelper.display({
            id: 'select',
            size: 0,
            cell: ({ row }) => (
                <Checkbox
                    checked={row.getIsSelected()}
                    onChange={row.getToggleSelectedHandler()}
                    aria-label={`Sélectionner ligne ${row.id}`}
                />
            )
        }),
        columnHelper.accessor('previewImageLink', {
            header: 'Image',
            enableSorting: false,
            size: 0,
            meta: {
                clickable: true
            },
            cell: (info) => {
                console.log('image de couverture', info.getValue());
                return <ImageLazy height={74} width={52} src={info.getValue()} />;
            }
        }),
        columnHelper.accessor('presentation.title', {
            header: 'Titre',
            id: 'presentation.title',
            enableSorting: true,
            minSize: 250,
            meta: {
                clickable: true
            },
            cell: (info) => <>{info.getValue()}</>
        }),
        columnHelper.accessor('event.startDateTime', {
            header: 'Date',
            id: 'event.startDateTime',
            enableSorting: true,
            minSize: 160,
            meta: {
                clickable: true
            },
            cell: (info) => <>{moment(info.getValue()).format('DD/MM/YYYY à HH:mm')}</>
        }),
        columnHelper.accessor('event.city', {
            header: 'Lieu',
            id: 'event.city',
            enableSorting: true,
            minSize: 160,
            meta: {
                clickable: true
            },
            cell: (info) => <>{info.getValue()}</>
        }),
        columnHelper.accessor('category', {
            header: 'Catégorie',
            enableSorting: true,
            meta: {
                clickable: true
            },
            cell: (info) => <>{CATEGORY_TYPE[info.getValue()]}</>
        }),

        columnHelper.accessor('price.priceTTC', {
            header: 'Prix vente TTC',
            id: 'price.priceTTC',
            minSize: 180,
            enableSorting: true,
            meta: {
                clickable: true
            },
            cell: (info) => <>{info.getValue() ? formatMoney(info.getValue()) : '-'}</>
        }),

        columnHelper.accessor('stockAmount', {
            header: 'Stock',
            size: 0,
            meta: {
                clickable: true
            },
            cell: (info) => <>{info.getValue()}</>
        }),
        columnHelper.display({
            id: 'actions',
            size: 0,
            header: 'Actions',
            meta: {
                style: {
                    textAlign: 'center'
                }
            },
            cell: (info) => {
                return (
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
                        <Button
                            variant="icon"
                            color="primary"
                            size="small"
                            hoverIcon={<Icon icon={<DupliquerFull />} size={20} color="primary" />}
                            icon={<Icon icon={<DupliquerLine />} size={20} color="primary" />}
                            onClick={() => handleDuplicateProduct(info.row.original.id)}
                        />
                        <Button
                            variant="icon"
                            color="primary"
                            size="small"
                            hoverIcon={<Icon icon={<PoubelleFull />} size={20} color="primary" />}
                            icon={<Icon icon={<PoubelleLine />} size={20} color="primary" />}
                            onClick={() => handleDeleteProduct(info.row.original.id)}
                        />
                    </div>
                );
            }
        }),
        columnHelper.accessor('publicLink', {
            header: 'Voir en ligne',
            meta: {
                clickable: false,
                style: {
                    textAlign: 'center'
                }
            },
            cell: (info) => (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    {info.row.original?.syncroStatus === 'error' ? (
                        <Icon icon={<SyncroError />} size={20} color="error" />
                    ) : (
                        <Button
                            variant="icon-hover"
                            color="grey-secondary"
                            icon={<Icon icon={<RoundedArrow />} size={24} />}
                            onClick={() => {
                                window.open(info.getValue(), '_blank');
                            }}
                        />
                    )}
                </div>
            )
        })
    ] as ColumnDef<AgendaProductTable>[];

    return (
        <div className="products-table-container">
            <div className="products-table-container-header">
                <div className="products-table-container-header-left">
                    <SimpleNumberCard labelForSingle={'Évènement'} labelForMultiple={'Évènements'} value={totalEvents} />
                    <LargeIconButton
                        icon={<AddDashed />}
                        label="Nouvel évènement"
                        onClick={() => navigate('/products-management/products/add?type=event')}
                    />
                </div>
            </div>
            <MutationTable
                key={'active'}
                needReload={needReload}
                columns={columns}
                mutationHook={useGetAgendaProductsMutation}
                onRowClick={(row) => navigate(`/products-management/products/${row.id}/edit`)}
                defaultSorting={{
                    field: 'id',
                    direction: 'desc'
                }}
                forceLoading={isLoading}
                headerTitle={'Liste des événements'}
                headerIcon={<ProductsCatalogLine />}
                onMetadata={handleMetadata}
                footer={ 
                    {
                        actions: [
                            {
                                label: 'Supprimer',
                                icon: <Icon icon={<PoubelleLine />} size={20} color="primary" />,
                                hoverIcon: <Icon icon={<PoubelleFull />} size={20} color="primary" />,
                                onClick: (lines: AgendaProductTable[]) => handleDeleteProducts(lines)
                            }
                        ]
                    }
                }
            />
        </div>
    );
}
